import axios from "axios";
import { axiosInstance } from "./AdminServices";
import { getPublicEvents } from "./EventMasterService";

// Ensure axiosInstance is properly initialized
if (!axiosInstance) {
  throw new Error(
    "axiosInstance is not initialized. Please check your AdminServices.js file."
  );
}

const API_URL = process.env.REACT_APP_API_URL;

const mapMasterEventToLegacy = (event) => ({
  id: String(event?.eventID || ""),
  name: event?.name || "",
  reg_date: event?.regDate || "",
  reg_end_date: event?.regEndDate || "",
  start_date: event?.startDate || "",
  end_date: event?.endDate || "",
  timings: event?.timings || "",
  status: event?.status || "Draft",
  short_description: event?.shortDescription || "",
  description: event?.description || "",
  location: event?.location || "",
  address: event?.address || "",
  map: event?.map || "",
  type: event?.type || "Retreat",
  wog: event?.wog || "",
  retreatEmail: event?.retreatEmail || "",
  contact: event?.contact || "",
  maxRegistrationCount: Number(event?.maxRegistrationCount ?? 0),
  paymentMethods: "",
});

// Fetch event data by ID with local JSON first, then Event Master API fallback
export const fetchEventData = async (eventID) => {
  const events = require("../source/events.json");
  console.log("Fetching event details for retreatID:", eventID);
  const retreatDetails = events.find(
    (event) => String(event.id) === String(eventID)
  );
  if (retreatDetails) {
    console.log("Retreat details found in local source:", retreatDetails);
    return retreatDetails;
  }

  try {
    const response = await getPublicEvents();
    const masterEvents = Array.isArray(response?.events) ? response.events : [];
    const matched = masterEvents.find(
      (event) => String(event?.eventID) === String(eventID)
    );
    if (matched) {
      const mapped = mapMasterEventToLegacy(matched);
      console.log("Retreat details found in Event Master API:", mapped);
      return mapped;
    }
  } catch (error) {
    console.error("Error fetching retreat details from Event Master API:", error);
  }

  throw new Error("Retreat not found.");
};

export const getGroups = async (filters) => {
  console.log("getGroups with filters", filters);
  const response = await axiosInstance.get(`/groups`, {
    params: filters,
  });
  // console.log("getGroups response", response);
  return response.data;
};

export const saveGroup = async (groupData, saveMode) => {
  console.log("Retreat Services saveGroup saveMode:", saveMode);

  try {
    if (saveMode === "UNLOAD") {
      // Fallback URL from axiosInstance if available
      const url = `${axiosInstance.defaults.baseURL || ""}/groups`;

      // Ensure proper JSON MIME type
      const blob = new Blob([JSON.stringify(groupData)], {
        type: "application/json",
      });

      let ok = false;
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.sendBeacon === "function"
      ) {
        ok = navigator.sendBeacon(url, blob);
        console.log("sendBeacon result:", ok);
        if (!ok) {
          // sendBeacon failed or unsupported, use axios
          try {
            // Ensure groups without a registrationID are saved as Draft before axios fallback
            if (
              !groupData.registrationID ||
              groupData.registrationID.trim() === "" ||
              groupData.registrationID === null ||
              groupData.registrationID === undefined
            ) {
              groupData.status = groupData.status || "Draft";
              groupData.registrationID = groupData.registrationID || "";
              console.log(
                "Fallback axios: saving group as Draft because registrationID is missing."
              );
            }
            const response = await axiosInstance.post("/groups", groupData, {
              withCredentials: true,
            });
            ok = true;
            console.log("Axios fallback successful", response.data);
          } catch (err) {
            console.error("Axios fallback failed", err);
            ok = false;
          }
        }
      } else {
        console.warn("sendBeacon is not supported in this environment.");
      }

      // Always return a consistent response object
      return { success: ok, data: groupData };
    }

    // Normal POST call
    const response = await axiosInstance.post(`/groups`, groupData);
    return response.data;
  } catch (error) {
    console.error("Error saving group:", error);

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
      if (error.response.status === 400) {
        console.error("Error saving group 400:", error.response.data?.error);
        throw new Error(
          "Error during registration | " +
            String(error.response.data?.error) +
            " | " +
            error.response.data?.message +
            " | " +
            JSON.stringify(error.response?.data?.data)
        );
      } else if (error.response.status === 500) {
        //throw new Error("Internal Server Error: Please try again later.");
        console.log(
          "Error during registration 500:",
          error.response.data?.error
        );
        throw new Error(
          "Error during registration :" ||
            error.response.data?.error ||
            "Internal Server Error 1"
        );
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log("Error message:", error.message);
        throw new Error("Error during registration : Internal Server Error 2");
      }
    } else if (error.code === "ERR_NETWORK") {
      // Handle network error specifically
      console.log("Network error:", error.message);
      throw new Error(
        "Network error: Please check your internet connection. Or try again later."
      );
    } else if (error.request) {
      // The request was made but no response was received
      console.log("Error request data:", error.request);
      throw new Error(
        "Error during registration : No response received from server"
      );
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log("Error message:", error.message);
      throw new Error("Error during registration : Internal Server Error 2");
    }
  }
};
export const updateGroupStatus = async (groupData, status) => {
  const registrationID = groupData.registrationID;
  const retreatID = groupData.retreatID;
  console.log(
    "\n\n ******* \n Retreat Services updateGroupStatus groupData",
    groupData
  );
  console.log("Retreat Services updateGroupStatus status", status);
  console.log("Retreat Services updateGroupStatus retreatID", retreatID);
  try {
    // Make sure to send both the status and retreatID in the request body
    const response = await axiosInstance.put(
      `/groupsStatus/${registrationID}`,
      {
        status,
        retreatID, // Adding the retreatID to the request body
        groupData,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating group status:", error);
    throw error;
  }
};

// Method to get retreat data using logged-in email and retreat ID
export const getRetreatData = async (
  retreatID,
  userEmail,
  userData,
  mode,
  registrationID
) => {
  console.log("$$$$$$$$$$$$ getRetreatData retreatID:", retreatID);
  console.log("$$$$$$$$$$$$ getRetreatData MODE :", mode);
  const email = userEmail || ""; // Extract email from userData
  //console.log("$$$$$$$$$$$$ getRetreatData email:", email);
  console.log("$$$$$$$$$$$$ getRetreatData registrationID:", registrationID);
  let response;
  let is404Error = false;
  try {
    if (mode === "USER") {
      response = await axiosInstance.get(`/groupByEmailAndRetreat`, {
        params: { email, retreatID, registrationID },
      });
    } else if (
      mode === "GUEST" &&
      email !== process.env.REACT_APP_GUEST_EMAIL
    ) {
      console.log("### inside if mode === GUEST");
      console.log("### getRetreatData mode === GUEST email:", email);
      console.log("### getRetreatData mode === GUEST retreatID:", retreatID);
      response = await axiosInstance.get(`/groupByEmailAndRetreat`, {
        params: { email, retreatID, registrationID },
      });
    }

    // console.log("$$$$$$$$$$$$ getRetreatData response:", response);
    if (response && response.data && response.data.groupInfo) {
      return response.data.groupInfo;
    }
  } catch (error) {
    //console.error("# Error fetching retreat data :", error);
    console.log(
      "##* Error fetching retreat data error.status #### :",
      error.status
    );

    // Handle 404 error specifically
    if (error.response && error.response.status === 404) {
      console.log("### inside if  404 :", error.status);
      is404Error = true;
    }
    // Handle 403 error specifically
    if (!is404Error && error.response && error.response.status === 403) {
      console.log("### inside if 403 error, retrying fetch...");
      try {
        response = await axiosInstance.get(`/groupByEmailAndRetreat`, {
          params: { email, retreatID },
        });
        if (response && response.data && response.data.groupInfo) {
          return response.data.groupInfo;
        }
      } catch (retryError) {
        console.error("### Retry failed:", retryError);
        throw retryError;
      }
    }

    // Handle other types of errors
    if (!is404Error && axios.isAxiosError(error)) {
      console.error("### inside if axios.isAxiosError(error) :", error);
      throw new Error(
        error.response ? error.response.data.message : "Network error"
      );
    } else {
      console.log("### inside else axios.isAxiosError(error) :");
    }
  }

  if (is404Error || mode === "ADMIN" || mode === "GUEST") {
    const defaultData = {
      userID: userData.userID || "",
      updatedBy: userData.userID || "",
      firstName: mode === "USER" ? userData?.firstName || "" : "",
      lastName: mode === "USER" ? userData?.lastName || "" : "",
      email:
        mode === "USER" && userData?.userType !== "G"
          ? userData?.email || ""
          : "",
      phone:
        mode === "USER" && userData?.userType !== "G"
          ? userData?.phone || ""
          : "",
      gender: mode === "USER" ? userData?.gender || "" : "",
      age: "0",
      pinNumber: "",
      numberOfParticipants: "1",
      address: mode === "USER" ? userData?.address || "" : "",
      city: mode === "USER" ? userData?.city || "" : "", // New field for city
      state: mode === "USER" ? userData?.state || "" : "", // New field for state
      zip: mode === "USER" ? userData?.zip || "" : "", // New field for zip
      country: mode === "USER" ? userData?.country || "" : "", // New field for country
      specialRequest: "",
      registrationID: "",
      emergencyContactNumber: "",
      emergencyContactPersonName: "",
      paymentMethod: "",
      paymentInfo: "",
      rooms: "1",
      shareRoom: false,
      roomNumber: "",
      status: "Draft",
      amount: "0",
      refundStatus: "",
      refundAmount: "0",
      paidAmount: "0",
      waiverSigned: "",
      retreatID: retreatID || "",
      createdAt: new Date().toISOString(),
      createdBy: userData.userID || "",
      createdSource: "Web",
      groupMembers: [
        {
          firstName: mode === "USER" ? userData.firstName || "" : "",
          lastName: mode === "USER" ? userData.lastName || "" : "",
          comments: "",
          relation: "Myself",
          email: mode === "USER" ? userData.email || "" : "",
          phone: mode === "USER" ? userData.phone || "" : "",
          gender: mode === "USER" ? userData.gender || "" : "",
          age: "0",
          memberID: "",
          retreatID: retreatID || "",
          isEdited: false,
        },
      ],
    };
    const eventData = await fetchEventData(retreatID);
    const paymentMethods =
      eventData && eventData.paymentMethods?.length
        ? eventData.paymentMethods.split(", ").map((method) => ({
            key: method,
            value: method,
          }))
        : [];
    console.log("### paymentMethods :", paymentMethods);
    if (paymentMethods.length > 0) {
      defaultData.paymentMethod = paymentMethods[0].key; // Set the first payment method as default
    }
    console.log("### inside if defaultData :", defaultData);
    return defaultData; // Return default data on 404 error or mode ADMIN/GUEST
  }
};

// Synchronously save group data to localStorage for beforeunload usage
export const saveGroupSync = (groupData) => {
  try {
    localStorage.setItem("draftGroupData", JSON.stringify(groupData));
    // Also attempt to send to server using sendBeacon if available
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        "/api/save-draft",
        new Blob([JSON.stringify(groupData)], { type: "application/json" })
      );
    }
    return true;
  } catch (error) {
    console.error("Error saving group data synchronously:", error);
    return false;
  }
};
//  registrationCountsByAgeGroup function
export const getRegistrationCounts = async (retreatID) => {
  const email = process.env.REACT_APP_GUEST_EMAIL; // Extract email from userData
  const password = process.env.REACT_APP_GUEST_PASSWORD; // Extract password from userData
  console.log(" registrationCounts", retreatID);
  const authenticateToken = localStorage.getItem("token");
  try {
    const response = await axios.get(`${API_URL}/registrationCounts`, {
      params: {
        authenticateToken,
        email,
        password,
        retreatID,
      },
    });
    console.log(" getRegistrationCounts response.data", response.data);
    return response?.data?.totalCount; // Return the response data
  } catch (error) {
    console.error("Error registrationCounts :", error);
    throw error;
  }
};

/**
 * Fetches the new registration status for a given retreat.
 *
 * @param {string} retreatID - The ID of the retreat to get the registration status for.
 * @returns {Promise<string>} - A promise that resolves to the new registration status of the retreat.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getNewRegistrationStatus = async (retreatID) => {
  const email = process.env.REACT_APP_GUEST_EMAIL; // Extract email from userData
  const password = process.env.REACT_APP_GUEST_PASSWORD; // Extract password from userData
  console.log(" getNewRegistrationStatus", retreatID);
  const authenticateToken = localStorage.getItem("token");
  try {
    const response = await axios.get(`${API_URL}/getNewRegistrationStatus`, {
      params: {
        authenticateToken,
        email,
        password,
        retreatID,
      },
    });
    console.log(" getNewRegistrationStatus response.data", response.data);
    return response?.data?.newRetreatStatus; // Return the response data
  } catch (error) {
    console.error("Error getNewRegistrationStatus :", error);
    return "Draft"; // Default to "Draft" if there's an error
  }
};
export const submitWaiver = async (formData) => {
  if (!(formData instanceof FormData)) {
    throw new Error("Invalid formData: Expected instance of FormData.");
  }
  console.log("submitWaiver called with formData:");

  try {
    const response = await axiosInstance.post(`/submit-waiver`, formData);
    console.log("submitWaiver response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "Error submitting waiver:",
      error?.response?.data || error.message
    );
    throw new Error(
      error?.response?.data?.message || "Waiver submission failed."
    );
  }
};

export const registerRetreat = async (formData) => {
  console.log("registerRetreat called with formData:", formData);
  try {
    let response;
    if (formData.type === "online") {
      response = await axios.post(`${API_URL}/sabbath/register`, formData);
    } else {
      response = await axiosInstance.post(`/retreat/register`, formData);
    }

    return response;
  } catch (error) {
    console.error("Error registering retreat", error);
    throw {
      code: error.response?.status || 500,
      details: error.response?.data?.details || "Error registering retreat.",
    };
  }
};
